/**
 * /closing/email — "Send Search Closing Email". Migrated from nuadvisory
 * `ClosingEmail.cshtml` + `closingEmailPage.js` (TinyMCE + jQuery).
 *
 * Content-only page: the authenticated shell (header/sidebar/footer) is
 * provided by `WebShellLayout` via `<Outlet>`, so this component returns ONLY
 * its page content — no shell wrapper (double-shell otherwise).
 *
 * Multi-source feature (Trello #811): recipients can be sourced from a
 * closed/canceled Thrive Search, a Contact Network, or a Candidate Tag.
 * The nuadvisory .NET API posts a Thrive note to each recipient server-side
 * (fire-and-forget in MessagingController) — this component only POSTs to
 * /api/Messaging.
 *
 * Auth: uses the `nu_assist_session` JWT cookie which the nuadvisory
 * `[Authorize]` policy accepts cross-origin. All advisory API fetch calls use
 * `credentials: "include"` — no explicit Authorization header.
 *
 * API base URL:
 *   - nu-assist FastAPI: VITE_API_URL env var (for /api/web/v2/searches-list)
 *   - nuadvisory .NET:   VITE_NUADVISORY_API_URL (for /api/Networks,
 *     /api/CandidateTags, /api/Messaging, /api/Templates)
 *
 * Three tabs (progressively enabled):
 *   1. Select Source — source toggle + source-specific list
 *   2. Candidates    — candidate table + checkbox selection
 *   3. Email Template — template dropdown + RichTextEditor + subject/CC + send
 *
 * Real sends are batched (BATCH_SIZE recipients per POST /api/Messaging) to
 * keep each request well under the 60 s ALB idle timeout. Progress and
 * per-recipient failure details are surfaced via toast after each batch.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useMe } from "@nu-assist/shared-fe";
import { useAdvisoryFetch, type FetchLike } from "../../context/AdvisoryClientContext";
import { NUADVISORY_BASE } from "../../lib/advisory";
import { Button } from "@nu-assist/ui";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { TablePagination } from "../../components/TablePagination";
import { StagePill } from "../../components/stages/StagePill";
import { type Placeholder, RichTextEditor, type RichTextEditorHandle } from "./RichTextEditor";
import { type ClosingSource, useClosingSourceUrl } from "./useClosingSourceUrl";

// ---------------------------------------------------------------------------
// Types — hand-typed from the .NET response shapes
// ---------------------------------------------------------------------------

interface CandidacyRow {
  id: string;
  contactId?: number;
  firstName: string;
  lastName: string;
  email: string | null;
  stage: string | null;
  rejectionReason: string | null;
}

// Nu-assist searches-list response shape (v2 API)
interface SearchesListItem {
  id_thrive: string;
  name: string;
  status: string | null;
  candidacies_count: number | null;
  thrive_created_at: string | null;
  days_open: number | null;
}
interface SearchesListResponse {
  items: SearchesListItem[];
  total: number;
  page: number;
  page_size: number;
  identity_resolved: boolean;
}

// Network types
export interface NetworkItem {
  id: string;
  name: string | null;
  memberCount: number;
}

interface NetworkContactsResponse {
  data: ThriveCandidacy[];
  total: number;
}

// Tag types
export interface TagItem {
  id: string;
  type: string;
  attributes: {
    name: string;
    color: string;
  };
}
interface TagsListResponse {
  data: TagItem[];
  meta: { count: number; offset: number };
}

// Thrive candidacy from .NET advisory API
interface ThriveCandidacy {
  id: string;
  attributes: {
    contact_name: string;
    contact_email?: string | null;
    contact_id?: number | null;
    stage?: string | null;
    rejection_reason?: string | null;
  };
}

/** Hand-typed from the .NET `TemplateViewModel`. */
interface EmailTemplate {
  id: number;
  name: string;
  content: string;
}

interface SelectedCandidate {
  firstName: string;
  lastName: string;
  email: string;
  contactId?: number;
}

type SortCriteria = "selected" | "firstName" | "lastName" | "email" | "stage" | "rejectionReason";

type SortDirection = "asc" | "desc";

type TabId = "search" | "candidates" | "template";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// 10 recipients × ~3 s max per .NET exponential-backoff retry = ~30 s headroom under the 60 s ALB idle timeout.
const BATCH_SIZE = 10;

const V2_PAGE_SIZE = 20;
const TAG_PAGE_SIZE = 50;
const CANDIDATES_PAGE_SIZE = 20;
const NETWORKS_PAGE_SIZE = 20;
/** How long the network / tag filter box waits before writing `q` to the URL. */
const FILTER_URL_DEBOUNCE_MS = 250;

// Every closing email goes out from the recruiter's own Gmail, one message per
// recipient. Google Workspace allows 2,000 messages a day per mailbox (rolling
// 24 h, all mail counts) and blocks the mailbox for up to 24 h past that, so a
// single send stops at 1,500 — the cap Google itself applies to mail merge.
const GMAIL_DAILY_LIMIT = 2000;
export const MAX_RECIPIENTS_PER_SEND = 1500;

function sendLimitMessage(alreadySent: number): string {
  const cap = MAX_RECIPIENTS_PER_SEND.toLocaleString();
  return alreadySent >= MAX_RECIPIENTS_PER_SEND
    ? `You already sent ${alreadySent.toLocaleString()} emails from this page. Gmail allows about ${GMAIL_DAILY_LIMIT.toLocaleString()} a day, so send the rest tomorrow.`
    : `Sends from this page are limited to ${cap} people a day. Send these first; the rest can go tomorrow.`;
}

const allNetworkContactsKey = (networkId: string | null) =>
  ["nuadvisory", "network-contacts-all", networkId] as const;

// Shared with EmailTemplateEditor so a template loaded in one surface is warm
// in the other.
const TEMPLATES_KEY = ["nuadvisory", "templates"] as const;

// Same token set the sibling editor offers; the .NET send path replaces these
// per-recipient.
const PLACEHOLDERS: Placeholder[] = [
  { label: "First Name", token: "{first_name}" },
  { label: "Last Name", token: "{last_name}" },
  { label: "User Email", token: "{user_email}" },
  { label: "User Name", token: "{user_name}" },
];

const API_BASE = import.meta.env.VITE_API_URL ?? "";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function chunk<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) result.push(arr.slice(i, i + size));
  return result;
}

/** Parse `contact_name` into first / middle / last the way the legacy JS did. */
export function parseFullName(contactName: string): {
  firstName: string;
  middleName: string;
  lastName: string;
} {
  const parts = contactName.trim().split(/\s+/).filter(Boolean);
  const firstName = parts[0] ?? "";
  const lastName = parts.length > 1 ? parts[parts.length - 1] : "";
  const middleName = parts.length > 2 ? parts[1] : "";
  return { firstName, middleName, lastName };
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

// V2 search fetch — calls the nu-assist FastAPI (status=closed_canceled)
async function fetchV2Searches(
  q: string,
  page: number,
  fetchImpl: FetchLike = fetch,
): Promise<SearchesListResponse> {
  const params = new URLSearchParams({
    status: "closed_canceled",
    owner: "all",
    page: String(page),
    page_size: String(V2_PAGE_SIZE),
    sort: "name",
    order: "asc",
  });
  if (q) params.set("q", q);
  const res = await fetchImpl(`${API_BASE}/api/web/v2/searches-list?${params.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`GET /api/web/v2/searches-list ${res.status}`);
  return res.json() as Promise<SearchesListResponse>;
}

// Neither nuadvisory list endpoint sorts (Thrive cannot), but both lists arrive whole, so the
// browser sorts them once, A to Z like the searches list. Networks without a name go last.
// Revisit (2026-10-27, decided over a server-side sort in nuadvisory, which would still have
// to load every page from Thrive): when tags or networks pass ~5,000 and the first load gets
// slow, when another page needs these lists, or when they must be joined with our own data.
// Then sync them into nu-assist the way searches are, rather than sorting in the .NET proxy.
const byName = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });

export function sortNetworksByName(networks: NetworkItem[]): NetworkItem[] {
  return [...networks].sort((a, b) => {
    if (a.name == null || b.name == null) {
      return (a.name == null ? 1 : 0) - (b.name == null ? 1 : 0);
    }
    return byName(a.name, b.name);
  });
}

export function sortTagsByName(tags: TagItem[]): TagItem[] {
  return [...tags].sort((a, b) => byName(a.attributes.name, b.attributes.name));
}

export async function fetchNetworks(fetchImpl: FetchLike = fetch): Promise<NetworkItem[]> {
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/Networks`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`GET /api/Networks ${res.status}`);
  return sortNetworksByName((await res.json()) as NetworkItem[]);
}

async function fetchNetworkContacts(
  networkId: string,
  page: number,
  pageSize: number,
  fetchImpl: FetchLike = fetch,
): Promise<NetworkContactsResponse> {
  const params = new URLSearchParams({
    limit: String(pageSize),
    offset: String((page - 1) * pageSize),
  });
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/Networks/${networkId}/contacts?${params.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`GET /api/Networks/${networkId}/contacts ${res.status}`);
  return res.json() as Promise<NetworkContactsResponse>;
}

// Every member of a network, for Select All: the table itself is server-paged.
async function fetchAllNetworkContacts(
  networkId: string,
  fetchImpl: FetchLike = fetch,
): Promise<NetworkContactsResponse> {
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/Networks/${networkId}/contacts`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`GET /api/Networks/${networkId}/contacts ${res.status}`);
  return res.json() as Promise<NetworkContactsResponse>;
}

// Load a single page of tags (no server-side filter — Thrive's name(like,…) is an exact
// case-sensitive match and name(eq,…) splits on commas, breaking tags like "Austin, TX").
async function fetchTagsPage(
  count: number,
  offset: number,
  fetchImpl: FetchLike = fetch,
): Promise<TagsListResponse> {
  const params = new URLSearchParams({ count: String(count), offset: String(offset) });
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/CandidateTags?${params.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`GET /api/CandidateTags ${res.status}`);
  return res.json() as Promise<TagsListResponse>;
}

// Fetch all tags at once: first page reveals the total; remaining pages load in parallel.
// 727 tags / 200 per page = 4 pages, ~1 s wall time when parallel.
export async function fetchAllTags(fetchImpl: FetchLike = fetch): Promise<TagItem[]> {
  const PAGE_SIZE = 200;
  const first = await fetchTagsPage(PAGE_SIZE, 0, fetchImpl);
  const total = first.meta.count;
  const pageCount = Math.ceil(total / PAGE_SIZE);
  if (pageCount <= 1) return sortTagsByName(first.data);
  const rest = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, i) =>
      fetchTagsPage(PAGE_SIZE, (i + 1) * PAGE_SIZE, fetchImpl).then((p) => p.data),
    ),
  );
  return sortTagsByName([...first.data, ...rest.flat()]);
}

async function fetchTagContacts(
  tagId: string,
  tagName: string,
  fetchImpl: FetchLike = fetch,
): Promise<ThriveCandidacy[]> {
  const url = `${NUADVISORY_BASE}/api/CandidateTags/${tagId}/contacts?tagName=${encodeURIComponent(tagName)}`;
  const res = await fetchImpl(url, { credentials: "include" });
  if (!res.ok) throw new Error(`GET /api/CandidateTags/${tagId}/contacts ${res.status}`);
  return res.json() as Promise<ThriveCandidacy[]>;
}

async function fetchTemplates(fetchImpl: FetchLike = fetch): Promise<EmailTemplate[]> {
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/Templates`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`GET /api/Templates ${res.status}`);
  return res.json() as Promise<EmailTemplate[]>;
}

interface SendEmailPayload {
  subject: string;
  content: string;
  destinations: SelectedCandidate[];
  carbonCopy: string[];
  isTest: boolean;
}

interface MessagingSendResult {
  requested: number;
  sent: number;
  failed: Array<{ email: string; reason: string }>;
}

async function sendEmail(
  payload: SendEmailPayload,
  fetchImpl: FetchLike = fetch,
): Promise<MessagingSendResult> {
  const body = {
    Subject: payload.subject,
    Content: payload.content,
    Destinations: payload.destinations.map((d) => ({
      FirstName: d.firstName,
      LastName: d.lastName,
      Email: d.email,
      ContactId: d.contactId ?? null,
    })),
    CarbonCopy: payload.carbonCopy,
    IsTest: payload.isTest,
  };
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/Messaging`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`POST /api/Messaging ${res.status}`);
  return res.json() as Promise<MessagingSendResult>;
}

// ---------------------------------------------------------------------------
// Candidacy row mappers
// ---------------------------------------------------------------------------

function dedupeByContactId(rows: CandidacyRow[]): CandidacyRow[] {
  const seen = new Set<number>();
  return rows.filter((r) => {
    if (r.contactId == null) return true;
    if (seen.has(r.contactId)) return false;
    seen.add(r.contactId);
    return true;
  });
}

function mapThriveCandidaciesToRows(candidacies: ThriveCandidacy[]): CandidacyRow[] {
  return candidacies.map((c) => {
    const { firstName, lastName } = parseFullName(c.attributes.contact_name ?? "");
    const contactId = c.attributes.contact_id ?? undefined;
    return {
      id: c.id ?? (contactId != null ? String(contactId) : crypto.randomUUID()),
      contactId,
      firstName,
      lastName,
      email: c.attributes.contact_email ?? null,
      stage: c.attributes.stage ?? null,
      rejectionReason: c.attributes.rejection_reason ?? null,
    };
  });
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

function compareCandidacyRows(
  a: CandidacyRow,
  b: CandidacyRow,
  criteria: SortCriteria,
  direction: SortDirection
): number {
  let comparison = 0;
  switch (criteria) {
    case "selected": {
      const aSel = a.email != null && a.email !== "";
      const bSel = b.email != null && b.email !== "";
      comparison = aSel === bSel ? 0 : aSel ? -1 : 1;
      break;
    }
    case "firstName":
      comparison = a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName);
      break;
    case "lastName":
      comparison = a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName);
      break;
    case "email":
      comparison = (a.email ?? "").localeCompare(b.email ?? "");
      break;
    case "stage":
      comparison = (a.stage ?? "").localeCompare(b.stage ?? "");
      break;
    case "rejectionReason":
      comparison = (a.rejectionReason ?? "").localeCompare(b.rejectionReason ?? "");
      break;
  }
  return direction === "asc" ? comparison : -comparison;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SendClosingEmail() {
  const me = useMe();
  const advisoryFetch = useAdvisoryFetch();
  const editorRef = useRef<RichTextEditorHandle>(null);
  const queryClient = useQueryClient();
  // Read after an await to tell whether the recruiter switched network meanwhile.
  const selectedNetworkIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  // Set on mount too: React StrictMode (dev) unmounts and remounts once, and a
  // cleanup-only effect would leave the ref false for the page's whole life.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabId>("search");

  // Source selection: the source, its name filter and its page live in the URL
  // (useClosingSourceUrl), so a link or a refresh opens the same list.
  const sourceUrl = useClosingSourceUrl();
  const sourceType: ClosingSource = sourceUrl.source;

  // The boxes are LOCAL drafts mirrored to the URL, never driven by it: React
  // Router applies URL changes as transitions, and a text input controlled by
  // a transition update loses its cursor and keystrokes (/pr-review #1101
  // iter 1). Search commits on Enter / its button; the network and tag boxes
  // commit 250 ms after the last key. `lastWrittenQ` tells this page's own
  // writes from a URL change made elsewhere (Back, the left-menu link).
  const [searchInput, setSearchInput] = useState(sourceType === "search" ? sourceUrl.q : "");
  const [filterDraft, setFilterDraft] = useState(sourceType !== "search" ? sourceUrl.q : "");
  const lastWrittenQ = useRef(sourceUrl.q);
  const commitQ = (text: string) => {
    lastWrittenQ.current = text.trim();
    sourceUrl.setQ(text);
  };

  // Search sub-panel state (v2 paginated): the committed query is the URL's.
  const searchQ = sourceType === "search" ? sourceUrl.q : "";
  const searchPage = sourceType === "search" ? sourceUrl.page : 1;

  // Network and tag lists filter on the draft (they are in memory), so the
  // list answers each key at once while the URL catches up.
  const draftFilter = filterDraft.trim();

  // Network sub-panel state
  const [selectedNetworkId, setSelectedNetworkId] = useState<string | null>(null);
  selectedNetworkIdRef.current = selectedNetworkId;
  const networkFilter = sourceType === "network" ? draftFilter : "";
  // While the typed filter has not reached the URL yet, the page it will
  // reset to (1) is the one shown, so typing never shows another page first.
  const filterPending = sourceType !== "search" && draftFilter !== sourceUrl.q;
  const networksPageRequested = sourceType === "network" && !filterPending ? sourceUrl.page : 1;

  // Tag sub-panel state
  const [selectedTag, setSelectedTag] = useState<{ id: string; name: string; color: string } | null>(null);
  const tagFilter = sourceType === "tag" ? draftFilter : "";
  const tagPageRequested = sourceType === "tag" && !filterPending ? sourceUrl.page : 1;

  // Candidates tab
  const [selectedSearchId, setSelectedSearchId] = useState<string | null>(null);
  const [isSearchCandidatesLoading, setIsSearchCandidatesLoading] = useState(false);
  const [isSearchCandidatesError, setIsSearchCandidatesError] = useState(false);
  const [candidacyRows, setCandidacyRows] = useState<CandidacyRow[]>([]);
  const [candidacyFilter, setCandidacyFilter] = useState("");
  // Map from row id → SelectedCandidate so cross-page selections survive server-paginated page turns.
  const [selectedCandidates, setSelectedCandidates] = useState<Map<string, SelectedCandidate>>(new Map());
  // Lower-cased emails already sent from this page, so the next round skips them.
  // Kept across source changes; lost on reload.
  const [sentEmails, setSentEmails] = useState<Set<string>>(new Set());
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  // True right after Select All; a manual toggle clears it.
  const [selectAllApplied, setSelectAllApplied] = useState(false);
  const [sortCriteria, setSortCriteria] = useState<SortCriteria>("selected");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [candidatesPage, setCandidatesPage] = useState(1);

  // Email Template tab
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [content, setContent] = useState("");
  const [subject, setSubject] = useState("");
  const [cc, setCc] = useState("");
  const [showSubjectWarning, setShowSubjectWarning] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Batched real-send state
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState<{ sent: number; total: number } | null>(null);

  // -------------------------------------------------------------------------
  // Queries
  // Pagination strategy differs by source:
  //   searches  → server-paginated (page + page_size forwarded to the FastAPI)
  //   networks  → server-paginated (limit + offset forwarded to nuadvisory; candidatesPage
  //               drives the query key so each page turn re-fetches the right slice) until the
  //               recruiter filters or sorts; then the whole network is loaded once and paged
  //               client-side (networkNeedsFullList)
  //   tags      → all pages fetched in parallel, filtered + sliced client-side (Thrive tag
  //               name(like,…) is case-sensitive; name(eq,…) splits on commas)
  // -------------------------------------------------------------------------

  const searchesV2Query = useQuery({
    queryKey: ["nuassist", "closing-searches-v2", searchQ, searchPage],
    queryFn: () => fetchV2Searches(searchQ, searchPage, advisoryFetch),
    enabled: sourceType === "search",
    staleTime: 30 * 1000,
  });

  const networksQuery = useQuery({
    queryKey: ["nuadvisory", "contact-networks"],
    queryFn: () => fetchNetworks(advisoryFetch),
    enabled: sourceType === "network",
    staleTime: 5 * 60 * 1000,
  });

  const allTagsQuery = useQuery({
    queryKey: ["nuadvisory", "all-candidate-tags"],
    queryFn: () => fetchAllTags(advisoryFetch),
    enabled: sourceType === "tag",
    staleTime: 5 * 60 * 1000,
  });

  // A server-paged network table can only filter or sort the 20 rows on screen. As soon as the
  // recruiter filters or sorts, load every member once and page in the browser instead, so the
  // table and Select All both work on the whole network.
  const networkNeedsFullList =
    sourceType === "network" && (candidacyFilter !== "" || sortCriteria !== "selected");
  const networkContactsQuery = useQuery({
    queryKey: ["nuadvisory", "network-contacts", selectedNetworkId, candidatesPage, CANDIDATES_PAGE_SIZE],
    // biome-ignore lint/style/noNonNullAssertion: enabled guard ensures selectedNetworkId is non-null
    queryFn: () => fetchNetworkContacts(selectedNetworkId!, candidatesPage, CANDIDATES_PAGE_SIZE, advisoryFetch),
    enabled: selectedNetworkId !== null && activeTab === "candidates" && !networkNeedsFullList,
    staleTime: 30 * 1000,
  });

  const allNetworkContactsQuery = useQuery({
    queryKey: allNetworkContactsKey(selectedNetworkId),
    // biome-ignore lint/style/noNonNullAssertion: enabled guard ensures selectedNetworkId is non-null
    queryFn: () => fetchAllNetworkContacts(selectedNetworkId!, advisoryFetch),
    enabled: selectedNetworkId !== null && activeTab === "candidates" && networkNeedsFullList,
    staleTime: 5 * 60 * 1000,
  });

  const tagContactsQuery = useQuery({
    queryKey: ["nuadvisory", "tag-contacts", selectedTag?.id],
    // biome-ignore lint/style/noNonNullAssertion: enabled guard ensures selectedTag is non-null
    queryFn: () => fetchTagContacts(selectedTag!.id, selectedTag!.name, advisoryFetch),
    enabled: selectedTag !== null && activeTab === "candidates",
    staleTime: 30 * 1000,
  });

  const templatesQuery = useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: () => fetchTemplates(advisoryFetch),
    staleTime: 5 * 60 * 1000,
  });

  // Populate candidacy rows when source data arrives
  useEffect(() => {
    if (activeTab !== "candidates") return;
    if (sourceType === "network" && networkNeedsFullList) {
      if (allNetworkContactsQuery.data)
        setCandidacyRows(mapThriveCandidaciesToRows(allNetworkContactsQuery.data.data));
    } else if (sourceType === "network" && networkContactsQuery.data) {
      setCandidacyRows(mapThriveCandidaciesToRows(networkContactsQuery.data.data));
    } else if (sourceType === "tag" && tagContactsQuery.data) {
      setCandidacyRows(dedupeByContactId(mapThriveCandidaciesToRows(tagContactsQuery.data)));
    }
  }, [
    activeTab,
    sourceType,
    networkNeedsFullList,
    networkContactsQuery.data,
    allNetworkContactsQuery.data,
    tagContactsQuery.data,
  ]);

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const sendMutation = useMutation({
    mutationFn: (payload: SendEmailPayload) => sendEmail(payload, advisoryFetch),
    onSuccess: () => {
      toast.success("Test email sent to you and CC.");
    },
    onError: (err) => {
      toast.error(`Error sending test email: ${err instanceof Error ? err.message : "unknown error"}`);
    },
  });

  // -------------------------------------------------------------------------
  // Derived data
  // -------------------------------------------------------------------------

  // Filter + sort as the table shows them; Select All uses the same order.
  const visibleOrder = (rows: CandidacyRow[]) => {
    const term = candidacyFilter.toLowerCase();
    return rows
      .filter(
        (r) =>
          !term ||
          r.firstName.toLowerCase().includes(term) ||
          r.lastName.toLowerCase().includes(term) ||
          (r.email ?? "").toLowerCase().includes(term),
      )
      .sort((a, b) => compareCandidacyRows(a, b, sortCriteria, sortDirection));
  };
  const sortedRows = visibleOrder(candidacyRows);
  // Unfiltered, unsorted network contacts are server-paged: candidacyRows holds exactly one page.
  const isServerPagedNetwork =
    sourceType === "network" && selectedNetworkId !== null && !networkNeedsFullList;
  const pagedRows = isServerPagedNetwork
    ? sortedRows
    : sortedRows.slice((candidatesPage - 1) * CANDIDATES_PAGE_SIZE, candidatesPage * CANDIDATES_PAGE_SIZE);

  const isSent = (email: string | null) => email != null && sentEmails.has(email.toLowerCase());
  const isSelectable = (r: CandidacyRow) => r.email != null && r.email !== "" && !isSent(r.email);
  const selectableIds = candidacyRows.filter(isSelectable).map((r) => r.id);
  // A server-paged network holds one page, so the list size comes from the server total.
  const listTotal = isServerPagedNetwork ? (networkContactsQuery.data?.total ?? 0) : selectableIds.length;
  // The cap is a daily Gmail budget, not a per-click one: what this page already sent counts.
  const selectionLimit = Math.max(0, MAX_RECIPIENTS_PER_SEND - sentEmails.size);
  // Over what this page may still send (not the fixed cap): after a send, a smaller list can
  // already be over what is left, and Select All must say it will pick only part of it.
  const overSendLimit = listTotal > selectionLimit;
  const selectedCount = selectedCandidates.size;
  const allSelected =
    selectedCount > 0 &&
    (selectAllApplied ||
      selectedCount >= selectionLimit ||
      (!isServerPagedNetwork && selectableIds.every((id) => selectedCandidates.has(id))));

  const searchItems = searchesV2Query.data?.items ?? [];
  const searchTotal = searchesV2Query.data?.total ?? 0;

  const allNetworks = networksQuery.data ?? [];
  const filteredNetworks = networkFilter
    ? allNetworks.filter((n) =>
        (n.name ?? "").toLowerCase().includes(networkFilter.toLowerCase()),
      )
    : allNetworks;
  // A link past the last page (or a page left behind while the draft
  // narrows the list) shows the last page rather than an empty one.
  const networksPage = Math.min(
    networksPageRequested,
    Math.max(1, Math.ceil(filteredNetworks.length / NETWORKS_PAGE_SIZE)),
  );
  const pagedNetworks = filteredNetworks.slice(
    (networksPage - 1) * NETWORKS_PAGE_SIZE,
    networksPage * NETWORKS_PAGE_SIZE,
  );

  const filteredTags = useMemo(() => {
    const all = allTagsQuery.data ?? [];
    if (!tagFilter) return all;
    const lower = tagFilter.toLowerCase();
    return all.filter((t) => t.attributes.name.toLowerCase().includes(lower));
  }, [allTagsQuery.data, tagFilter]);

  const tagPage = Math.min(
    tagPageRequested,
    Math.max(1, Math.ceil(filteredTags.length / TAG_PAGE_SIZE)),
  );

  const filteredPagedTags = useMemo(
    () => filteredTags.slice((tagPage - 1) * TAG_PAGE_SIZE, tagPage * TAG_PAGE_SIZE),
    [filteredTags, tagPage],
  );

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  // Filter and sort belong to one list: a new source or network starts unfiltered, in the
  // default order (which also puts a network back on cheap server paging).
  function resetCandidateView() {
    setCandidacyFilter("");
    setSortCriteria("selected");
    setSortDirection("asc");
    setSelectAllApplied(false);
  }

  function resetSource() {
    resetCandidateView();
    setSelectedSearchId(null);
    setSelectedNetworkId(null);
    setSelectedTag(null);
    setSelectedCandidates(new Map());
    setCandidacyRows([]);
    setCandidatesPage(1);
    setIsSearchCandidatesLoading(false);
    setIsSearchCandidatesError(false);
  }

  // Drops the old source's filter and page in the same URL write; the reset
  // below runs from the URL, so a Back or a link that changes the source
  // resets the same way a click on the radio does.
  const handleSourceChange = (type: ClosingSource) => {
    // Clear the draft now too, so the new list never renders one frame
    // filtered by the old source's text.
    setFilterDraft("");
    sourceUrl.setSource(type);
  };

  const shownSource = useRef(sourceType);
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs on a source change only; resetSource is redefined every render
  useEffect(() => {
    if (shownSource.current === sourceType) return;
    shownSource.current = sourceType;
    resetSource();
    setActiveTab("search");
    lastWrittenQ.current = sourceUrl.q;
    setSearchInput(sourceType === "search" ? sourceUrl.q : "");
    setFilterDraft(sourceType !== "search" ? sourceUrl.q : "");
  }, [sourceType]);

  // The same source, its query changed elsewhere (Back, an edited link):
  // show it in the box. The page's own writes are skipped by `lastWrittenQ`.
  useEffect(() => {
    if (sourceUrl.q === lastWrittenQ.current) return;
    lastWrittenQ.current = sourceUrl.q;
    if (sourceType === "search") setSearchInput(sourceUrl.q);
    else setFilterDraft(sourceUrl.q);
  }, [sourceUrl.q, sourceType]);

  // The network / tag draft reaches the URL 250 ms after the last key.
  // biome-ignore lint/correctness/useExhaustiveDependencies: commitQ is redefined every render; the draft and the URL value are what matter
  useEffect(() => {
    if (sourceType === "search" || draftFilter === sourceUrl.q) return;
    const timer = setTimeout(() => commitQ(draftFilter), FILTER_URL_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draftFilter, sourceType, sourceUrl.q]);

  const handleSelectSearch = (searchId: string) => {
    resetCandidateView();
    setSelectedSearchId(searchId);
    setSelectedCandidates(new Map());
    setCandidacyRows([]);
    setCandidatesPage(1);
    setActiveTab("candidates");
  };

  const handleSelectNetwork = (id: string) => {
    resetCandidateView();
    setSelectedNetworkId(id);
    setSelectedCandidates(new Map());
    setCandidacyRows([]);
    setCandidatesPage(1);
    setActiveTab("candidates");
  };

  const handleSelectTag = (tag: { id: string; name: string; color: string }) => {
    resetCandidateView();
    setSelectedTag(tag);
    setSelectedCandidates(new Map());
    setCandidacyRows([]);
    setCandidatesPage(1);
    setActiveTab("candidates");
  };

  // For the search source: candidates come from the advisory API (Thrive
  // candidacies for a given search). Fetches once per selectedSearchId;
  // skips the call if rows are already populated (avoids re-fetch on tab return).
  useEffect(() => {
    if (sourceType !== "search" || selectedSearchId === null || activeTab !== "candidates") return;
    if (candidacyRows.length > 0) return;
    let cancelled = false;
    setIsSearchCandidatesLoading(true);
    setIsSearchCandidatesError(false);
    advisoryFetch(`${NUADVISORY_BASE}/api/Searches/candidacies/${selectedSearchId}`, {
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) throw new Error(`GET /api/Searches/candidacies/${selectedSearchId} ${r.status}`);
        return r.json() as Promise<ThriveCandidacy[]>;
      })
      .then((data) => {
        if (!cancelled) {
          setCandidacyRows(mapThriveCandidaciesToRows(data));
          setIsSearchCandidatesLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error("Failed to load candidates.");
          setIsSearchCandidatesError(true);
          setIsSearchCandidatesLoading(false);
        }
      });
    return () => { cancelled = true; };
  // candidacyRows intentionally omitted: it's reset by handleSelectSearch on
  // every new search pick, which re-enters this effect via selectedSearchId.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceType, selectedSearchId, activeTab, advisoryFetch]);

  const toggleCandidate = (row: CandidacyRow, checked: boolean) => {
    if (checked && selectedCandidates.size >= selectionLimit) {
      toast.warning(sendLimitMessage(sentEmails.size));
      return;
    }
    setSelectAllApplied(false);
    setSelectedCandidates((prev) => {
      const next = new Map(prev);
      if (checked && isSelectable(row) && row.email != null) {
        next.set(row.id, { firstName: row.firstName, lastName: row.lastName, email: row.email, contactId: row.contactId });
      } else {
        next.delete(row.id);
      }
      return next;
    });
  };

  // Select All picks up to MAX_RECIPIENTS_PER_SEND people not yet sent, in table
  // order. For a network the table holds one page, so it loads every member first.
  const handleSelectAll = async () => {
    if (allSelected) {
      setSelectAllApplied(false);
      setSelectedCandidates(new Map());
      return;
    }
    if (selectionLimit === 0) {
      toast.warning(sendLimitMessage(sentEmails.size));
      return;
    }
    let rows = sortedRows;
    if (isServerPagedNetwork && selectedNetworkId !== null) {
      setIsSelectingAll(true);
      try {
        const all = await queryClient.fetchQuery({
          queryKey: allNetworkContactsKey(selectedNetworkId),
          queryFn: () => fetchAllNetworkContacts(selectedNetworkId, advisoryFetch),
          staleTime: 5 * 60 * 1000,
        });
        // Switched network while loading: these members are not the list on screen.
        if (selectedNetworkIdRef.current !== selectedNetworkId) return;
        rows = visibleOrder(mapThriveCandidaciesToRows(all.data));
      } catch {
        toast.error("Could not load every member of this network. Try again.");
        return;
      } finally {
        if (mountedRef.current) setIsSelectingAll(false);
      }
      if (!mountedRef.current) return;
    }
    const next = new Map<string, SelectedCandidate>();
    for (const r of rows) {
      if (next.size >= selectionLimit) break;
      if (isSelectable(r) && r.email != null)
        next.set(r.id, { firstName: r.firstName, lastName: r.lastName, email: r.email, contactId: r.contactId });
    }
    setSelectedCandidates(next);
    setSelectAllApplied(next.size > 0);
  };

  const handleSort = (criteria: SortCriteria) => {
    if (sortCriteria === criteria) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCriteria(criteria);
      setSortDirection("asc");
    }
    setCandidatesPage(1);
  };

  const handleTemplateChange = (id: string) => {
    setSelectedTemplateId(id);
    const template = (templatesQuery.data ?? []).find((t) => String(t.id) === id);
    const html = template ? template.content : "";
    setContent(html);
    editorRef.current?.setContent(html);
  };

  const handleReset = () => {
    setSubject("");
    setContent("");
    setCc("");
    setSelectedTemplateId("");
    setShowSubjectWarning(false);
    editorRef.current?.setContent("");
  };

  const handleSendTest = () => {
    sendMutation.mutate({
      subject,
      content,
      destinations: [],
      carbonCopy: cc.split(",").map((e) => e.trim()).filter(Boolean),
      isTest: true,
    });
  };

  const handleSendToCandidates = () => {
    if (selectedCount === 0) {
      toast.warning("Select at least one candidate before sending.");
      return;
    }
    if (!subject.trim()) {
      setShowSubjectWarning(true);
      return;
    }
    setShowSubjectWarning(false);
    setShowConfirm(true);
  };

  const handleConfirmSend = useCallback(async () => {
    const destinations = Array.from(selectedCandidates.values());
    const ccList = cc.split(",").map((e) => e.trim()).filter(Boolean);
    const total = destinations.length;
    const batches = chunk(destinations, BATCH_SIZE);
    let sentSoFar = 0;
    const allFailed: Array<{ email: string; reason: string }> = [];

    setShowConfirm(false);
    setIsSending(true);
    setSendProgress({ sent: 0, total });

    for (const batch of batches) {
      try {
        const result = await sendEmail(
          { subject, content, destinations: batch, carbonCopy: ccList, isTest: false },
          advisoryFetch,
        );
        sentSoFar += result.sent ?? 0;
        allFailed.push(...(result.failed ?? []));
      } catch (_err) {
        allFailed.push(...batch.map((d) => ({ email: d.email, reason: "Network error" })));
      }
      setSendProgress({ sent: sentSoFar, total });
      if (!mountedRef.current) return;
    }

    setIsSending(false);
    setSendProgress(null);
    if (!mountedRef.current) return;

    const failedEmails = new Set(allFailed.map((f) => f.email.toLowerCase()));
    setSentEmails((prev) => {
      const next = new Set(prev);
      for (const d of destinations)
        if (!failedEmails.has(d.email.toLowerCase())) next.add(d.email.toLowerCase());
      return next;
    });

    if (allFailed.length === 0) {
      toast.success(`All ${sentSoFar} emails sent successfully.`);
      setSelectedCandidates(new Map());
    } else if (sentSoFar > 0) {
      setSelectedCandidates((prev) => {
        const next = new Map(prev);
        for (const [id, candidate] of next) {
          if (!failedEmails.has(candidate.email.toLowerCase())) next.delete(id);
        }
        return next;
      });
      toast.warning(
        `${sentSoFar} of ${total} sent — ${allFailed.length} failed. Check your Gmail Sent folder before retrying the remaining recipients.`,
      );
    } else {
      toast.error(
        `Send failed — 0 of ${total} went out. Check your network or Gmail account, then retry.`,
      );
    }
  }, [subject, content, cc, selectedCandidates, advisoryFetch]);

  // Defensive narrow — WebShellLayout's AuthGuard already wraps this.
  if (me.status !== "authenticated") return null;

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const tabClass = (id: TabId) =>
    `px-4 py-2 text-body font-medium border-b-2 transition-colors ${
      activeTab === id
        ? "border-accent text-accent"
        : "border-transparent text-text-secondary hover:text-text hover:border-border"
    }`;

  const sortIndicator = (criteria: SortCriteria) =>
    sortCriteria === criteria ? (sortDirection === "asc" ? " ▲" : " ▼") : " ↕";

  const guardTabSwitch = (tab: TabId) => {
    const hasSourceSelected =
      (sourceType === "search" && selectedSearchId !== null) ||
      (sourceType === "network" && selectedNetworkId !== null) ||
      (sourceType === "tag" && selectedTag !== null);
    if (!hasSourceSelected) {
      toast.warning("Select a source first.");
      return;
    }
    setActiveTab(tab);
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div data-testid="send-closing-email" className="p-12">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <h1 className="min-w-0 text-h1 text-text">Send Search Closing Email</h1>
          <Link
            to="/closing/email-template"
            data-testid="closing-email-edit-template-link"
            className="inline-flex items-center justify-center gap-1.5 rounded-md px-3.5 py-2 text-label font-semibold transition-colors no-underline border border-border bg-surface text-text-secondary hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          >
            Edit templates
          </Link>
        </div>

        <p className="mb-2 text-body text-text-secondary">
          After we close a search, we send an email to most of the Contact who
          were involved in the search, for a better candidate and source
          experience.
        </p>
        <p className="mb-6 text-body text-text-secondary">
          <a
            href="https://drive.google.com/file/d/1dFc_NCkNuD5iQTU7bSkQvRbCndauLEL3/view?usp=drive_link"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline"
          >
            Watch the training video.
          </a>
        </p>

        {/* Tabs */}
        <div className="mb-6 flex border-b border-border" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "search"}
            className={tabClass("search")}
            onClick={() => setActiveTab("search")}
          >
            Select Source
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "candidates"}
            className={tabClass("candidates")}
            onClick={() => guardTabSwitch("candidates")}
          >
            Candidates
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "template"}
            className={tabClass("template")}
            onClick={() => guardTabSwitch("template")}
          >
            Email Template
          </button>
        </div>

        {/* Tab: Select Source */}
        {activeTab === "search" && (
          <div>
            {/* Source type toggle */}
            <fieldset className="mb-6">
              <legend className="mb-2 text-label font-medium text-text">Source</legend>
              <div className="flex gap-6">
                {(["search", "network", "tag"] as const).map((type) => (
                  <label key={type} className="flex cursor-pointer items-center gap-2 text-body text-text">
                    <input
                      type="radio"
                      name="sourceType"
                      value={type}
                      checked={sourceType === type}
                      onChange={() => handleSourceChange(type)}
                      className="h-4 w-4 accent-accent"
                    />
                    {type === "search" ? "Search" : type === "network" ? "Contact Network" : "Tag"}
                  </label>
                ))}
              </div>
            </fieldset>

            {/* Search sub-panel */}
            {sourceType === "search" && (
              <div>
                <div className="mb-4 flex gap-2">
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitQ(searchInput);
                      }
                    }}
                    placeholder="Search by name or Thrive ID"
                    className="w-full max-w-md rounded-md border border-border bg-surface px-3 py-2 text-body text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      commitQ(searchInput);
                    }}
                  >
                    Search
                  </Button>
                </div>

                {searchesV2Query.isLoading && (
                  <p className="text-body text-text-muted">Loading searches…</p>
                )}
                {searchesV2Query.isError && (
                  <p className="text-caption text-error">Error occurred while fetching searches.</p>
                )}

                {!searchesV2Query.isLoading && !searchesV2Query.isError && (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="px-4 py-3 text-label font-semibold text-text">Name</th>
                        <th className="px-4 py-3 text-label font-semibold text-text">Status</th>
                        <th className="px-4 py-3 text-label font-semibold text-text">Days Open</th>
                        <th className="px-4 py-3 text-label font-semibold text-text">Candidacies</th>
                        <th className="px-4 py-3 text-label font-semibold text-text">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchItems.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-6 text-center text-body text-text-muted">
                            No data available
                          </td>
                        </tr>
                      ) : (
                        searchItems.map((s) => (
                          <tr
                            key={s.id_thrive}
                            className="border-b border-border last:border-0 hover:bg-row-hover"
                          >
                            <td className="px-4 py-3 text-body text-text">{s.name}</td>
                            <td className="px-4 py-3 text-body text-text-secondary">{s.status ?? "—"}</td>
                            <td className="px-4 py-3 text-body text-text-secondary">
                              {s.days_open != null ? s.days_open : "—"}
                            </td>
                            <td className="px-4 py-3 text-body text-text-secondary">
                              {s.candidacies_count != null ? s.candidacies_count : "—"}
                            </td>
                            <td className="px-4 py-3">
                              <Button type="button" onClick={() => handleSelectSearch(s.id_thrive)}>
                                Select
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}

                <TablePagination
                  total={searchTotal}
                  page={searchPage}
                  pageSize={V2_PAGE_SIZE}
                  onPageChange={sourceUrl.setPage}
                  noun="search"
                  nounPlural="searches"
                  testId="closing-email-searches-pagination"
                />
              </div>
            )}

            {/* Contact Network sub-panel */}
            {sourceType === "network" && (
              <div>
                <div className="mb-4">
                  <input
                    type="text"
                    value={filterDraft}
                    onChange={(e) => {
                      setFilterDraft(e.target.value);
                    }}
                    placeholder="Filter networks by name"
                    className="w-full max-w-md rounded-md border border-border bg-surface px-3 py-2 text-body text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>
                {networksQuery.isLoading && (
                  <p className="text-body text-text-muted">Loading networks…</p>
                )}
                {networksQuery.isError && (
                  <p className="text-caption text-error">Error occurred while fetching networks.</p>
                )}
                {!networksQuery.isLoading && !networksQuery.isError && (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="px-4 py-3 text-label font-semibold text-text">Name</th>
                        <th className="px-4 py-3 text-label font-semibold text-text">Members</th>
                        <th className="px-4 py-3 text-label font-semibold text-text">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedNetworks.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="px-4 py-6 text-center text-body text-text-muted">
                            {networkFilter ? "No networks match your filter" : "No networks available"}
                          </td>
                        </tr>
                      ) : (
                        pagedNetworks.map((n) => (
                          <tr
                            key={n.id}
                            className="border-b border-border last:border-0 hover:bg-row-hover"
                          >
                            <td className="px-4 py-3 text-body text-text">{n.name ?? "—"}</td>
                            <td className="px-4 py-3 text-body text-text-secondary">{n.memberCount}</td>
                            <td className="px-4 py-3">
                              <Button
                                type="button"
                                onClick={() => handleSelectNetwork(n.id)}
                              >
                                Select
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
                <TablePagination
                  total={filteredNetworks.length}
                  page={networksPage}
                  pageSize={NETWORKS_PAGE_SIZE}
                  onPageChange={sourceUrl.setPage}
                  noun="network"
                  testId="closing-email-networks-pagination"
                />
              </div>
            )}

            {/* Tag sub-panel */}
            {sourceType === "tag" && (
              <div>
                <div className="mb-4">
                  <input
                    type="text"
                    value={filterDraft}
                    onChange={(e) => {
                      setFilterDraft(e.target.value);
                    }}
                    placeholder="Filter tags by name"
                    className="w-full max-w-md rounded-md border border-border bg-surface px-3 py-2 text-body text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>

                {allTagsQuery.isLoading && (
                  <p className="text-body text-text-muted">Loading tags…</p>
                )}
                {allTagsQuery.isError && (
                  <p className="text-caption text-error">Error occurred while fetching tags.</p>
                )}
                {!allTagsQuery.isLoading && !allTagsQuery.isError && (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="px-4 py-3 text-label font-semibold text-text">Color</th>
                        <th className="px-4 py-3 text-label font-semibold text-text">Tag Name</th>
                        <th className="px-4 py-3 text-label font-semibold text-text">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPagedTags.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="px-4 py-6 text-center text-body text-text-muted">
                            No tags available
                          </td>
                        </tr>
                      ) : (
                        filteredPagedTags.map((tag) => (
                          <tr
                            key={tag.id}
                            className="border-b border-border last:border-0 hover:bg-row-hover"
                          >
                            <td className="px-4 py-3">
                              <span
                                className="inline-block h-4 w-4 rounded-full border border-border"
                                style={{ backgroundColor: tag.attributes.color ?? "#ccc" }}
                                aria-label={tag.attributes.color ?? "no color"}
                              />
                            </td>
                            <td className="px-4 py-3 text-body text-text">{tag.attributes.name}</td>
                            <td className="px-4 py-3">
                              <Button
                                type="button"
                                onClick={() =>
                                  handleSelectTag({
                                    id: tag.id,
                                    name: tag.attributes.name,
                                    color: tag.attributes.color,
                                  })
                                }
                              >
                                Select
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
                <TablePagination
                  total={filteredTags.length}
                  page={tagPage}
                  onPageChange={sourceUrl.setPage}
                  pageSize={TAG_PAGE_SIZE}
                  noun="tag"
                  testId="closing-email-tags-pagination"
                />
              </div>
            )}
          </div>
        )}

        {/* Tab: Candidates */}
        {activeTab === "candidates" && (
          <div>
            <div className="mb-4 flex items-center gap-4">
              <span className="text-body text-text">
                Candidates Selected: <strong>{selectedCount.toLocaleString()}</strong>
                {overSendLimit && (
                  <span className="text-text-muted"> of {selectionLimit.toLocaleString()} allowed now</span>
                )}
              </span>
              <Button type="button" onClick={() => setActiveTab("template")}>
                Next
              </Button>
            </div>

            {overSendLimit && (
              <div
                role="note"
                data-testid="closing-email-send-limit"
                className="mb-4 flex items-start gap-2 rounded-md border border-warning-strong bg-warning-muted px-3 py-2 text-body text-warning-ink"
              >
                <span>
                  This list has {listTotal.toLocaleString()} people. Closing emails go out from your Gmail, which
                  allows about {GMAIL_DAILY_LIMIT.toLocaleString()} emails a day, so this page sends to at most{" "}
                  {MAX_RECIPIENTS_PER_SEND.toLocaleString()} people and you can select{" "}
                  {selectionLimit.toLocaleString()} more now. Select All picks the next people who have not been
                  sent yet, in table order and matching your filter; send the rest tomorrow. People you sent to stay
                  marked Sent until you leave this page; the count starts again if you reload it.
                </span>
              </div>
            )}

            <div className="mb-4">
              <input
                type="text"
                value={candidacyFilter}
                onChange={(e) => { setCandidacyFilter(e.target.value); setCandidatesPage(1); }}
                // Select All uses the filter and sort it was clicked with; freeze them while it loads.
                disabled={isSelectingAll}
                placeholder="Filter by name or email"
                className="w-full max-w-md rounded-md border border-border bg-surface px-3 py-2 text-body text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            {(sourceType === "search" && isSearchCandidatesLoading) ||
            (sourceType === "network" &&
              (networkNeedsFullList ? allNetworkContactsQuery.isLoading : networkContactsQuery.isLoading)) ||
            (sourceType === "tag" && tagContactsQuery.isLoading) ? (
              <p className="text-body text-text-muted">
                {sourceType === "network" && networkNeedsFullList
                  ? "Loading every member of this network to filter and sort it…"
                  : "Loading candidates…"}
              </p>
            ) : (sourceType === "search" && isSearchCandidatesError) ||
              (sourceType === "network" &&
                (networkNeedsFullList ? allNetworkContactsQuery.isError : networkContactsQuery.isError)) ||
              (sourceType === "tag" && tagContactsQuery.isError) ? (
              <div className="flex items-center gap-3">
                <p className="text-caption text-error">Error occurred while fetching candidates.</p>
                {sourceType === "network" && networkNeedsFullList && (
                  // Back to the paged view, which does not need the whole network.
                  <Button type="button" variant="secondary" onClick={resetCandidateView}>
                    Clear filter and sort
                  </Button>
                )}
              </div>
            ) : (
              <table className="w-full text-left">
                <thead className="sticky top-0 bg-surface">
                  <tr className="border-b border-border">
                    <th className="px-4 py-3">
                      <button
                        type="button"
                        onClick={handleSelectAll}
                        disabled={isSelectingAll || (selectionLimit === 0 && !allSelected)}
                        className="text-caption text-accent hover:text-accent-hover disabled:opacity-60"
                      >
                        {isSelectingAll
                          ? "Selecting…"
                          : selectionLimit === 0 && !allSelected
                            ? "Daily limit reached"
                            : allSelected
                            ? "Deselect All"
                            : overSendLimit
                              ? `Select next ${selectionLimit.toLocaleString()}`
                              : "Select All"}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-label font-semibold text-text">
                      <button type="button" onClick={() => handleSort("firstName")} disabled={isSelectingAll} className="hover:text-accent disabled:opacity-60">
                        Name{sortIndicator("firstName")}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-label font-semibold text-text">
                      <button type="button" onClick={() => handleSort("stage")} disabled={isSelectingAll} className="hover:text-accent disabled:opacity-60">
                        Stage{sortIndicator("stage")}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-label font-semibold text-text">
                      <button type="button" onClick={() => handleSort("rejectionReason")} disabled={isSelectingAll} className="hover:text-accent disabled:opacity-60">
                        Rejection Reason{sortIndicator("rejectionReason")}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-body text-text-muted">
                        No data available
                      </td>
                    </tr>
                  ) : (
                    pagedRows.map((r) => {
                      const emailable = r.email != null && r.email !== "";
                      const sent = isSent(r.email);
                      return (
                        <tr
                          key={r.id}
                          className="border-b border-border last:border-0 hover:bg-row-hover"
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedCandidates.has(r.id)}
                              disabled={!emailable || sent}
                              onChange={(e) => toggleCandidate(r, e.target.checked)}
                              className="h-4 w-4 accent-accent disabled:opacity-40"
                              aria-label={`Select ${r.firstName} ${r.lastName}`}
                            />
                          </td>
                          <td className="px-4 py-3 text-body text-text">
                            <div className="flex items-center gap-2">
                              <span>{r.firstName} {r.lastName}</span>
                              {sent && (
                                <span className="rounded-pill bg-success-muted px-2 py-0.5 text-micro font-semibold text-success-ink">
                                  Sent
                                </span>
                              )}
                            </div>
                            <div className="text-caption text-text-muted">{r.email ?? "no email"}</div>
                          </td>
                          <td className="px-4 py-3">
                            <StagePill stage={r.stage} />
                          </td>
                          <td className="px-4 py-3 text-body text-text-secondary">
                            {r.rejectionReason ?? "—"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}

            <TablePagination
              total={
                isServerPagedNetwork
                  ? (networkContactsQuery.data?.total ?? 0)
                  : sortedRows.length
              }
              page={candidatesPage}
              pageSize={CANDIDATES_PAGE_SIZE}
              onPageChange={setCandidatesPage}
              noun="candidate"
              testId="closing-email-candidates-pagination"
            />
          </div>
        )}

        {/* Tab: Email Template */}
        {activeTab === "template" && (
          <div className="max-w-2xl">
            <div className="mb-4 flex flex-col gap-1">
              <label htmlFor="template-dropdown" className="text-label font-medium text-text">
                Select Template
              </label>
              <select
                id="template-dropdown"
                value={selectedTemplateId}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="rounded-sm border border-border bg-surface px-2 py-2 text-body text-text focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="">Select a template</option>
                {(templatesQuery.data ?? []).map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4 flex flex-col gap-1">
              <span className="text-label font-medium text-text">Template Content</span>
              <RichTextEditor
                ref={editorRef}
                initialContent=""
                onChange={setContent}
                placeholders={PLACEHOLDERS}
              />
            </div>

            <div className="mb-4 flex flex-col gap-1">
              <label htmlFor="email-subject" className="text-label font-medium text-text">
                Subject
              </label>
              <input
                id="email-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject"
                required
                className="rounded-sm border border-border bg-surface px-3 py-2 text-body text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
              />
              {showSubjectWarning && (
                <p className="text-caption text-error">Subject is required to send the email.</p>
              )}
            </div>

            <div className="mb-4 flex flex-col gap-1">
              <label htmlFor="email-cc" className="text-label font-medium text-text">
                CC
              </label>
              <input
                id="email-cc"
                type="text"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="Enter CC email addresses separated by commas"
                className="rounded-sm border border-border bg-surface px-3 py-2 text-body text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <p className="mb-3 text-body text-text-secondary" aria-live="polite">
              {sendProgress && `Sending… ${sendProgress.sent} of ${sendProgress.total} sent`}
            </p>

            <div className="flex gap-2">
              <Button variant="secondary" type="button" onClick={handleReset} disabled={isSending}>
                Reset
              </Button>
              <Button
                variant="secondary"
                type="button"
                onClick={handleSendTest}
                disabled={sendMutation.isPending || isSending}
              >
                Send Test to Me and CC
              </Button>
              <Button
                type="button"
                onClick={handleSendToCandidates}
                disabled={sendMutation.isPending || isSending}
              >
                {isSending ? "Sending…" : "Send to Candidates"}
              </Button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={showConfirm}
        title="Confirm Sending Emails"
        message={`Send this email to ${selectedCount.toLocaleString()} ${selectedCount === 1 ? "person" : "people"} from your Gmail? Keep this page open until it finishes. After sending, check your Sent mail in Gmail to confirm they all sent.`}
        confirmLabel="Send"
        busy={isSending}
        onConfirm={handleConfirmSend}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
}
